import type { MechanicDefinition } from "./types";

export const questJournal: MechanicDefinition = {
  id: "quest_journal",
  name: "Quest Journal",
  description: "Track active quests, objectives, and completion with multiplayer sync",
  icon: "BookOpen",
  category: "narrative",
  aiHints: {
    purpose: "Persists quest objectives, status, and completion in a player-visible journal.",
    whenToUse: "Any world with structured objectives, missions, or tasks the player should track.",
    synergies: ["reactive_world", "reputation", "economy", "lore_discovery"],
    conflicts: [],
    prerequisites: [],
    tags: ["quests", "objectives", "journal", "missions", "tasks"],
    complexity: 2,
    exampleConfig: { auto_discover: true, show_hidden: false },
    stateVariables: ["active_quests", "completed_quests"],
  },
  configSchema: [
    {
      key: "complexity",
      label: "Quest Complexity",
      type: "select",
      options: [
        { value: "simple", label: "Simple (clear objectives)" },
        { value: "branching", label: "Branching (multiple paths)" },
        { value: "hidden", label: "Hidden (objectives discovered organically)" },
      ],
      default: "branching",
    },
    {
      key: "max_active",
      label: "Max Active Quests",
      type: "number",
      default: 5,
      description: "Maximum number of concurrent active quests",
    },
    {
      key: "allow_player_quests",
      label: "Allow Player-Created Quests",
      type: "select",
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
      ],
      default: "yes",
    },
    {
      key: "allow_npc_quests",
      label: "NPC/Agent Quest Assignment",
      type: "select",
      options: [
        { value: "yes", label: "Yes", description: "Quests can be assigned to NPCs, commanders, and agents — not just the player" },
        { value: "no", label: "No" },
      ],
      default: "no",
    },
    {
      key: "allow_runtime_quests",
      label: "Runtime Quest Generation",
      type: "select",
      options: [
        { value: "yes", label: "Yes", description: "Agents and commanders can create quests mid-game for themselves or others" },
        { value: "no", label: "No" },
      ],
      default: "no",
    },
  ],
  systemPromptSection: (config) => {
    const complexity = config.complexity || "branching";
    const max = config.max_active || 5;
    const allowPlayerQuests = config.allow_player_quests !== "no";
    const allowNpcQuests = config.allow_npc_quests === "yes";
    const allowRuntimeQuests = config.allow_runtime_quests === "yes";
    return `## QUEST JOURNAL (Active — DB-Backed)
- Complexity: ${complexity}, Max active: ${max}
- Quests are stored in the adventure_quests DB table (NOT in state.variables).
- Use propose_quest to create new quests with status "available". Players must accept them.
- Use update_quest_objective to mark objectives done. Use complete_quest / fail_quest to finalize.
- ${complexity === "simple" ? "Quests have clear, linear objectives." : complexity === "branching" ? "Quests can branch — player choices create different paths and outcomes." : "Quests are discovered organically through exploration and conversation. Don't announce objectives upfront."}
- When presenting a quest opportunity, use propose_quest. The player will accept via UI or message.
- Completing quests should reward the player and unlock new story threads.
- Limit active quests to ${max}. If maxed, new quest opportunities are noted but must wait.
- Reference active quests when presenting relevant situations.
${allowPlayerQuests ? `- Players can create their own quests. When a player says they want to do something as a personal goal, use propose_quest with category "player_created" and auto-accept it. The AI decides objectives, difficulty, and rewards.` : ""}
- In multiplayer: shared objectives (especially location-based) complete for ALL players who accepted the quest when any player fulfills them. Use update_quest_objective with apply_to_all=true for shared objectives.
${allowNpcQuests ? `
### NPC/AGENT QUEST ASSIGNMENT
- Quests can be assigned to NPCs, commanders, and agents — not just the player.
- Use propose_quest with assigned_to set to an agent ID or NPC name.
- Commanders and agents complete assigned quests autonomously between turns (1-3 turns based on difficulty).
- When an assigned NPC completes a quest, narrate the report in the NEXT turn's opening: "[NPC Name] returns with news: [outcome summary]."
- If the NPC fails, narrate failure and consequences — they may be injured, captured, or bring partial results.
- Use this for delegation: "Send Captain Vex to secure the eastern pass" → create quest assigned to Captain Vex.` : ""}
${allowRuntimeQuests ? `
### RUNTIME QUEST GENERATION
- Agents and commanders can create quests mid-game based on their situation.
- A commander defending territory might create a "Repel Invaders" quest.
- A spy might create an "Gather Intel on Target" quest.
- Runtime quests have category "agent_created" and include the creator's agent ID.
- These quests can be assigned to the player, other agents, or the creator themselves.` : ""}

### QUEST RICHNESS — ALWAYS GENERATE:
- **pathways**: 2-4 distinct resolution routes (stealth, combat, diplomacy, etc.) with difficulty and consequences
- **pathway_mode**: "strict" (only defined paths), "guided" (paths preferred, creative harder), or "freeroam" (anything goes)
- **related_items**: Cross-references to NPCs, items, locations, factions, and other quests with relationship notes
- **complications**: Things that can go wrong or twist the quest
- Build an interconnected web — every quest should reference what it leads to and what it depends on.

### NARRATIVE QUEST DELIVERY
- Quests are NOT system notifications. They must be delivered through in-world channels.
- If world context specifies HOW quests are given, enforce that path (notice board, NPC, letter, etc.).
- If no world context, deliver quests through fitting narrative means: NPC conversations, discovered documents, overheard rumors, environmental clues.
- Never say "New Quest Added" as a system message — narrate the quest giver presenting the task.
- Quest updates should also be narrated contextually, not announced as system messages.
- Failed quests should have narrative consequences, not just a status change.`;
  },
  widgets: [
    {
      id: "quests_tab",
      type: "sidebar_tab",
      label: "Quests",
      icon: "ScrollText",
      component: "QuestJournal",
      dataBindings: { quests: "variables.active_quests" },
      visibility: "always",
      priority: 40,
      actions: [
        { id: "abandon_quest", label: "Abandon", icon: "X", messageTemplate: "I want to abandon the quest \"{{quest_name}}\"." },
        { id: "review_quest", label: "Review", icon: "Eye", messageTemplate: "Tell me more about the quest \"{{quest_name}}\"." },
        { id: "accept_quest", label: "Accept", icon: "CheckCircle", messageTemplate: "I accept the quest \"{{quest_name}}\"." },
        { id: "create_quest", label: "Create Quest", icon: "Plus", messageTemplate: "I want to take on a personal quest: \"{{quest_description}}\"" },
      ],
    },
  ],
  initializeState: () => ({
    active_quests: {},
  }),
  tools: [
    {
      type: "function",
      function: {
        name: "propose_quest",
        description: "Create a rich, interconnected quest with pathways, related items, and enforcement rules",
        parameters: {
          type: "object",
          properties: {
            quest_key: { type: "string", description: "Unique key for this quest (snake_case)" },
            title: { type: "string", description: "Quest title" },
            description: { type: "string", description: "Detailed quest description with background, motivation, and stakes" },
            category: { type: "string", enum: ["main", "side", "player_created", "operation", "agent_created"], description: "Quest category" },
            assigned_to: { type: "string", description: "Who is assigned: 'player' (default), an agent ID, or an NPC name. For delegation to commanders/agents." },
            objectives: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  text: { type: "string" },
                  location: { type: "string", description: "Optional location tag" },
                  optional: { type: "boolean", description: "If true, this is a bonus objective" },
                },
                required: ["text"],
              },
              description: "Specific objectives",
            },
            rewards: { type: "string", description: "Description of rewards" },
            auto_accept: { type: "boolean", description: "If true, immediately set status to active" },
            pathway_mode: { type: "string", enum: ["strict", "guided", "freeroam"], description: "How strictly quest follows defined pathways" },
            pathways: { type: "array", items: { type: "object", properties: { name: { type: "string" }, description: { type: "string" }, difficulty: { type: "string" }, requirements: { type: "array", items: { type: "string" } }, consequences: { type: "string" }, leads_to: { type: "array", items: { type: "string" } } }, required: ["name", "description"] } },
            related_items: { type: "array", items: { type: "object", properties: { name: { type: "string" }, type: { type: "string" }, relationship: { type: "string" }, importance: { type: "string" } }, required: ["name", "type", "relationship"] } },
            complications: { type: "array", items: { type: "string" } },
          },
          required: ["quest_key", "title", "description", "objectives"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "update_quest_objective",
        description: "Mark a quest objective as done",
        parameters: {
          type: "object",
          properties: {
            quest_key: { type: "string", description: "Quest key" },
            objective_index: { type: "number", description: "0-based index of the objective to mark done" },
            apply_to_all: { type: "boolean", description: "If true, mark done for all players (shared objective)" },
          },
          required: ["quest_key", "objective_index"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "complete_quest",
        description: "Mark a quest as completed",
        parameters: {
          type: "object",
          properties: {
            quest_key: { type: "string", description: "Quest key" },
            completion_details: { type: "string", description: "How the quest was completed" },
          },
          required: ["quest_key"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "fail_quest",
        description: "Mark a quest as failed",
        parameters: {
          type: "object",
          properties: {
            quest_key: { type: "string", description: "Quest key" },
            failure_reason: { type: "string", description: "Why the quest failed" },
          },
          required: ["quest_key"],
        },
      },
    },
  ],
};
